
export interface PackageVisit {
  id: number;
  visit_date: string;
  patient_name: string;
  patient: number;
  source_of_reference: string;
  additional_referral_details: string;
  contact_number: string;
  treating_doctor: string;
  treatment_advice: string;
  treatment_advice_id: number;
  treating_doctor_id: number;
  patient_mrn_number: string;
  patient_gender: string;
  counselor_name: string | null;
  counseling_session_id: number | null;
  is_active_package_invoice_refund_request?: boolean;
  counseling_date: string | null;
  counseling_stage: string | null;
  status: 'Completed' | 'InProgress' | 'ToDo';
  /** Invoice ID - null if no invoice created yet */
  invoice_id: number | null;
  /** Package ID - null if no package assigned yet */
  package_id: number | null;
  /** Whether the package invoice can be closed */
  can_close_package_invoice: boolean;
  /** Whether the package is already closed */
  is_package_closed?: boolean;
  /** Whether there are unsettled package invoices */
  has_unsettled_package_invoice?: boolean;
  /** Clinic ID */
  clinic_id?: number | null;
  /** Clinic Name */
  clinic?: string | null;
}

export interface PackageVisitResponse {
  meta: {
    total_count: number;
    limit: number;
    offset: number;
    status_counts: {
      completed_count: number;
      in_progress_count: number;
      todo_count: number;
    };
  };
  objects: PackageVisit[];
}



// ---------------------------------------------
// Doctor list (DOCTOR role) response interfaces
// ---------------------------------------------
export interface DoctorDepartment {
  id: number;
  name: string;
  clinic__id: number;
  clinic__name: string;
  name_label: string;
  tenant__name: string;
  is_clinical: boolean;
}

export interface DoctorUser {
  id: number;
  email: string;
  first_name: string;
  gender__name: string; // e.g. 'MALE'
  last_name: string;
  username: string;
  role: string; // e.g. 'DOCTOR'
  departments: DoctorDepartment[];
  appointment_type: string; // e.g. 'APPOINTMENT'
  is_active: boolean;
  user_type: string; // e.g. 'DOCTOR'
  language: number; // language id
  language_code: string; // e.g. 'en'
  language_name: string; // e.g. 'English'
  is_locked: boolean;
  gender__name_label: string; // localized gender label
  role_label: string; // localized role label
}

export interface DoctorListMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface DoctorListResponse {
  meta: DoctorListMeta;
  objects: DoctorUser[];
}


/**
 * Build query string with pagination, filters and optional search term.
 */
export function buildCounselingQuery(params: {
  page: number;
  pageSize: number;
  filters?: Record<string, unknown>;
  searchTerm?: string;
  patientId?: number;
}): string {
  const { page, pageSize, filters = {}, searchTerm, patientId } = params;
  const offset = page * pageSize;
  // Normalize specific filter values
  const normalizedFilters: Record<string, unknown> = { ...filters };
  if (normalizedFilters.status === 'InProgress') {
    normalizedFilters.status = 'in_progress';
  }

  const queryObj: Record<string, unknown> = {
    limit: pageSize,
    offset,
    ...normalizedFilters,
    ...(searchTerm ? { search_filter: searchTerm } : {}),
    ...(patientId ? { patient_id: patientId } : {}),
  };
  return Object.entries(queryObj)
    .filter(([, v]) => v !== undefined && v !== null && String(v) !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
}

/**
 * Map API status to chip props and translation key
 */
export function mapStatusToChip(status: PackageVisit['status']): {
  name: 'completed' | 'InProgress' | 'todo';
  tKey: 'completed' | 'incomplete' | 'to_do';
} {
  if (status === 'Completed') return { name: 'completed', tKey: 'completed' };
  if (status === 'InProgress') return { name: 'InProgress', tKey: 'incomplete' };
  return { name: 'todo', tKey: 'to_do' };
}

/**
 * Determine action based on status
 */
export function mapStatusToAction(status: PackageVisit['status']): 'view' | 'edit' | 'add' {
  if (status === 'Completed') return 'view';
  if (status === 'InProgress') return 'edit';
  return 'add';
}

/**
 * Format ISO date to en-GB dd/mm/yyyy
 */
export function formatGBDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-GB');
  } catch {
    return isoDate;
  }
}

/**
 * Extract status counts safely
 */
export function getStatusCounts(meta?: PackageVisitResponse['meta']): {
  todo: number;
  completed: number;
  inProgress: number;
} {
  return {
    todo: meta?.status_counts.todo_count ?? 0,
    completed: meta?.status_counts.completed_count ?? 0,
    inProgress: meta?.status_counts.in_progress_count ?? 0,
  };
}



export function getCounselingPatientList(query: string): string {
  return `/counseling-sessions/counseling-list/?${query}`;
}



export function getDrList(): string {
  return '/users/?role=DOCTOR';
}

